import { Check, X } from 'lucide-react';

import { EnrichmentStatusPill } from '@/features/catalog/components/EnrichmentStatusPill';
import { formatEnrichment } from '@/features/catalog/components/enrichmentFormat';
import type { PendingEnrichment } from '@/features/catalog/types';

interface Props {
  enrichments: PendingEnrichment[];
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  busyId: string | null;
}

export function EnrichmentList({
  enrichments,
  onApprove,
  onReject,
  busyId,
}: Props): JSX.Element {
  if (enrichments.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-ink-300 dark:border-white/15 bg-ink-50/40 dark:bg-white/[0.02] p-8 text-center text-sm text-ink-500 dark:text-ink-400">
        No enrichment suggestions.
      </div>
    );
  }

  return (
    <ul className="space-y-3">
      {enrichments.map((enrichment) => {
        const formatted = formatEnrichment(enrichment);
        const isPending = enrichment.status === 'pending';
        const isBusy = busyId === enrichment.id;

        return (
          <li
            key={enrichment.id}
            className="flex items-start justify-between gap-4 rounded-xl border border-ink-200/70 dark:border-white/10 bg-white dark:bg-ink-900 p-4 shadow-soft transition hover:border-ink-300"
          >
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-[10px] font-semibold tracking-[0.18em] text-ink-500 dark:text-ink-400">
                  {formatted.label.toUpperCase()}
                </span>
                <EnrichmentStatusPill status={enrichment.status} />
                {enrichment.confidence !== null && (
                  <span className="rounded-full bg-accent-violet/10 px-2 py-0.5 font-mono text-[10px] font-semibold tracking-wide text-accent-violet ring-1 ring-inset ring-accent-violet/30">
                    {Math.round(enrichment.confidence * 100)}% CONF
                  </span>
                )}
              </div>
              <p className="mt-1.5 truncate font-display text-sm font-semibold text-ink-900 dark:text-ink-50">
                {formatted.title}
              </p>
              {formatted.body && (
                <p className="mt-1 text-sm leading-relaxed text-ink-600 dark:text-ink-300">{formatted.body}</p>
              )}
              {enrichment.error && (
                <p className="mt-1 font-mono text-[11px] text-accent-crimson">
                  Apply failed: {enrichment.error}
                </p>
              )}
            </div>

            {isPending && (
              <div className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  disabled={isBusy}
                  onClick={() => onReject(enrichment.id)}
                  className="inline-flex h-8 items-center gap-1 rounded-md border border-accent-crimson/30 bg-white dark:bg-ink-900 px-2.5 text-xs font-medium text-accent-crimson transition hover:-translate-y-px hover:bg-accent-crimson/5 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <X className="h-3 w-3" />
                  Reject
                </button>
                <button
                  type="button"
                  disabled={isBusy}
                  onClick={() => onApprove(enrichment.id)}
                  className="btn-primary h-8 px-2.5 text-xs"
                >
                  <Check className="h-3 w-3" />
                  Approve
                </button>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
